import { createContext, useContext } from "react";
import { action, computed, observable } from "mobx";
import {
  IActivationJSON,
  IKtpScanFields,
  IKtpScanResult,
  IMerchantJSON,
  KtpScanReason,
  KtpScanStatus,
} from "src/interfaces";
import { AsyncStore, PassportStore } from "src/stores";
import { asyncRequest } from "src/decorators";
import { Activation, Merchant, User } from "src/models";
import { cleanObject, composeFiles, fromEnv } from "src/helpers";
import {
  BusinessType,
  Citizenship,
  COUNTRIES,
  IDType,
  INDONESIA,
  PaymentMethod,
  PLACE_OF_BIRTH,
  POB_OTHERS,
  ValidationRegex,
} from "src/constants";

const { National, International } = Citizenship;
const { KTP, KITAS } = IDType;
const { Individual, Corporate } = BusinessType;
const { Basic, Card } = PaymentMethod;

const KTP_SCAN_POLL_INTERVAL_MS =
  Number(fromEnv("REACT_APP_KTP_SCAN_POLL_INTERVAL_MS")) || 1000;
// safety net on top of the server's own 10 s limit
const KTP_SCAN_POLL_LIMIT_MS =
  Number(fromEnv("REACT_APP_KTP_SCAN_POLL_LIMIT_MS")) || 20000;

// the scan got no answer (start failed, no scan found, or polling hit the deadline):
// ask for a new upload and leave manual entry to the backend's attempt count
const KTP_SCAN_NO_ANSWER: IKtpScanResult = {
  status: "FAILED_RETRY",
  reason: "TECHNICAL_ERROR",
};

const KTP_SCAN_FIELDS: (keyof IKtpScanFields)[] = [
  "id_number",
  "id_name",
  "pob",
  "dob",
  "gender",
  "occupation",
];

export type KtpScanError = KtpScanReason | "INVALID_FORMAT";

interface AdditionalOnboardingData {
  pob: string;
  pob_others: string;
  dob: string | null;
  director_email: string;
  selfie: string;
  liveness_verified: boolean;
  owner_nationality: string;
  gender: string;
  occupation: string;
}

export class OwnerInfoState extends AsyncStore<OwnerInfoState> {
  constructor(
    public translate?: (...args: any) => string,
    public passportStore?: PassportStore
  ) {
    super();
  }

  sortedPoB = [...PLACE_OF_BIRTH].sort((a, b) => a.localeCompare(b));

  cities = [POB_OTHERS, ...this.sortedPoB].map((city) => {
    return {
      id: city,
      key: city,
    };
  });

  @observable
  placeOfBirthOptions = this.cities;

  @action
  applyPlaceOfBirthSearch = (input: string) => {
    if (input.trim().length === 0) {
      this.placeOfBirthOptions = this.cities;
      return;
    }

    this.placeOfBirthOptions = this.cities.filter(({ key }) => {
      if (key === POB_OTHERS) return true;

      return key.includes(input.toUpperCase());
    });
  };

  nationalities = Object.entries(COUNTRIES)
    .filter(([value]) => value !== INDONESIA)
    .map(([value, label]) => ({
      id: value,
      key: label,
    }));

  @observable
  nationalityOptions = this.nationalities;

  @action
  applyNationalitySearch = (input: string) => {
    const keyword = input.trim().toLowerCase();

    this.nationalityOptions =
      keyword.length === 0
        ? this.nationalities
        : this.nationalities.filter(({ key }) =>
            key.toLowerCase().includes(keyword)
          );
  };

  @observable
  selectedCitizenship: Citizenship = National;

  @observable
  isIdTypeKitas = this.passportStore?.outlet?.json?.id_type === KITAS;

  @observable
  citizenshipOptions = [
    {
      id: National,
      key: this.translate?.("owner_info.citizenship.wni"),
      selected: !this.isIdTypeKitas,
    },
    {
      id: International,
      key: this.translate?.("owner_info.citizenship.wna"),
      selected: this.isIdTypeKitas,
    },
  ];

  @observable
  fileUpload: any = {
    ktp: {
      url: "",
      temp: undefined,
      mediaType: "",
      metadata: {},
    },
    kitas: {
      url: "",
      temp: undefined,
      mediaType: "",
      metadata: {},
    },
    passport: {
      url: "",
      temp: undefined,
      mediaType: "",
      metadata: {},
    },
    npwp: {
      url: "",
      temp: undefined,
      mediaType: "",
      metadata: {},
    },
    selfie: {
      url: "",
      temp: undefined,
      mediaType: "",
      metadata: {},
    },
  };

  @observable
  form: Partial<IMerchantJSON> = {
    director_name: this.sanitizedDirectorName || "",
    id_name: "",
    id_number: "",
    id_type: "",
    npwp: "",
    npwp_name: "",
    npwp_url: "",
    headquarters_address: "",
    files: this.passportStore?.outlet?.files! || [],
  };

  @observable
  additionalForm: Partial<AdditionalOnboardingData> = {
    pob: "",
    pob_others: "",
    dob: "",
    director_email: "",
    selfie: "",
    liveness_verified: false,
    owner_nationality: INDONESIA,
    gender: "",
    occupation: "",
  };

  @observable
  errorFields: any = {
    director_name: {
      error: false,
      helperText: undefined,
    },
    id_name: {
      error: this.passportStore?.outlet?.idName ? false : undefined,
      helperText: undefined,
    },
    id_number: {
      error: this.passportStore?.outlet.idNumber ? false : undefined,
      helperText: undefined,
    },
    npwp: {
      error: this.passportStore?.outlet?.npwp ? false : undefined,
      helperText: undefined,
    },
    npwp_name: {
      error: this.passportStore?.outlet?.npwpName ? false : undefined,
      helperText: undefined,
    },
    headquarters_address: {
      error: this.passportStore?.outlet?.headquartersAddress
        ? false
        : undefined,
      helperText: undefined,
    },
    ktp_upload: {
      error: undefined,
      helperText: undefined,
    },
    kitas_upload: {
      error: undefined,
      helperText: undefined,
    },
    passport_upload: {
      error: undefined,
      helperText: undefined,
    },
    npwp_upload: {
      error: this.passportStore?.outlet.npwpUrl ? false : undefined,
      helperText: undefined,
    },
    pob: {
      error: undefined,
      helperText: undefined,
    },
    pob_others: {
      error: undefined,
      helperText: undefined,
    },
    dob: {
      error: undefined,
      helperText: undefined,
    },
    director_email: {
      error: undefined,
      helperText: undefined,
    },
    selfie_upload: {
      error: undefined,
      helperText: undefined,
    },
    liveness_verified: {
      error: undefined,
      helperText: undefined,
    },
    owner_nationality: {
      error: false,
      helperText: undefined,
    },
    gender: {
      error: undefined,
      helperText: undefined,
    },
    occupation: {
      error: undefined,
      helperText: undefined,
    },
  };

  @observable
  isOssEnabled = fromEnv("REACT_APP_OSS_ENABLED") === "true";

  @observable
  isKtpOcrEnabled = fromEnv("REACT_APP_KTP_OCR_ENABLED") === "true";

  @observable
  isOwnerNationalityEnabled =
    fromEnv("REACT_APP_OWNER_NATIONALITY_ENABLED") === "true";

  get isDirectorEmailRequired() {
    return this.isKtpOcrEnabled && this.isWni;
  }

  get isKtpScanActive() {
    return this.isKtpOcrEnabled && this.isWni;
  }

  @observable
  ktpScanStatus?: KtpScanStatus;

  // page load is still asking for the last scan
  @observable
  isKtpScanLoading = false;

  // KTP fields filled by a successful scan; shown read-only when the scan left some empty
  @observable
  ktpScanFilledFields: (keyof IKtpScanFields)[] = [];

  onKtpScanError?: (error: KtpScanError) => void;

  // bumped to remount the KTP picker, which keeps its own preview of the last file
  @observable
  ktpUploadKey = 0;

  private ktpScanPollTimer?: ReturnType<typeof setTimeout>;
  private ktpScanPollRun = 0;

  get isKtpScanning() {
    return this.isKtpScanActive && this.ktpScanStatus === "PROCESSING";
  }

  get isKtpFieldsHidden() {
    if (!this.isKtpScanActive) return false;

    switch (this.ktpScanStatus) {
      case "PROCESSING":
      case "FAILED_RETRY":
        return true;
      case "SUCCESS":
        return this.ktpScanFilledFields.length === KTP_SCAN_FIELDS.length;
      case "FAILED_MAX_ATTEMPTS":
      case "SKIPPED":
        return false;
      default:
        // no scan yet: show the fields only when a KTP was already uploaded before OCR,
        // and only once page load knows that, so a finished scan does not flash them
        return this.isKtpScanLoading || !this.fileUpload.ktp.url;
    }
  }

  isKtpFieldLocked(field: keyof IKtpScanFields) {
    return (
      this.isKtpScanActive &&
      this.ktpScanStatus === "SUCCESS" &&
      this.ktpScanFilledFields.includes(field)
    );
  }

  @action
  async startKtpScan(fileType: string) {
    this.cancelKtpScanPolling();
    this.ktpScanStatus = "PROCESSING";
    const run = this.ktpScanPollRun;

    try {
      const result = await this.passportStore!.startKtpScan(fileType);
      if (run !== this.ktpScanPollRun) return;
      this.applyKtpScanResult(result, Date.now() + KTP_SCAN_POLL_LIMIT_MS);
    } catch {
      if (run === this.ktpScanPollRun)
        this.applyKtpScanResult(KTP_SCAN_NO_ANSWER);
    }
  }

  // page load: resume a running scan, refill a successful one, or open manual entry
  @action
  async loadKtpScan() {
    if (!this.isKtpScanActive) return;

    const run = this.ktpScanPollRun;
    this.isKtpScanLoading = true;
    try {
      const result = await this.passportStore!.getKtpScanResult();
      if (run !== this.ktpScanPollRun || !result) return;
      if (result.status === "FAILED_RETRY") {
        // the merchant has to upload again
        this.clearKtpUpload();
        return;
      }
      this.applyKtpScanResult(
        result,
        Date.now() + KTP_SCAN_POLL_LIMIT_MS,
        false
      );
    } catch {
      // keep the form as it is
    } finally {
      this.isKtpScanLoading = false;
    }
  }

  @action
  applyKtpScanResult(result: IKtpScanResult, deadline = 0, showError = true) {
    this.ktpScanStatus = result.status;

    switch (result.status) {
      case "PROCESSING":
        this.scheduleKtpScanPoll(deadline);
        return;
      case "SUCCESS":
        this.fillKtpScanFields(result.fields || {});
        return;
      case "FAILED_RETRY":
        this.clearKtpUpload();
        if (showError)
          this.onKtpScanError?.(result.reason || "TECHNICAL_ERROR");
        return;
      default:
        this.ktpScanFilledFields = [];
    }
  }

  scheduleKtpScanPoll(deadline: number) {
    const run = this.ktpScanPollRun;

    this.ktpScanPollTimer = setTimeout(async () => {
      if (Date.now() >= deadline) {
        this.applyKtpScanResult(KTP_SCAN_NO_ANSWER);
        return;
      }

      try {
        const result = await this.passportStore!.getKtpScanResult();
        if (run !== this.ktpScanPollRun) return;
        this.applyKtpScanResult(result || KTP_SCAN_NO_ANSWER, deadline);
      } catch {
        // a failed check is retried until the deadline
        if (run === this.ktpScanPollRun) this.scheduleKtpScanPoll(deadline);
      }
    }, KTP_SCAN_POLL_INTERVAL_MS);
  }

  cancelKtpScanPolling() {
    this.ktpScanPollRun += 1;
    if (this.ktpScanPollTimer) clearTimeout(this.ktpScanPollTimer);
    this.ktpScanPollTimer = undefined;
  }

  @action
  fillKtpScanFields(fields: IKtpScanFields) {
    this.ktpScanFilledFields = KTP_SCAN_FIELDS.filter((key) => !!fields[key]);

    const pob = fields.pob?.toUpperCase() || "";
    const isKnownPob = PLACE_OF_BIRTH.includes(pob);

    this.form.id_number = fields.id_number || "";
    this.form.id_name = fields.id_name || "";
    this.additionalForm.pob = pob ? (isKnownPob ? pob : POB_OTHERS) : "";
    this.additionalForm.pob_others = pob && !isKnownPob ? fields.pob : "";
    this.additionalForm.dob = fields.dob || "";
    this.additionalForm.gender = fields.gender || "";
    this.additionalForm.occupation = fields.occupation || "";

    [
      "id_number",
      "id_name",
      "pob",
      "pob_others",
      "dob",
      "gender",
      "occupation",
    ].forEach((key) => {
      this.errorFields[key] = { error: undefined, helperText: undefined };
    });
    this.ktpScanFilledFields.forEach((key) => {
      this.errorFields[key].error = false;
    });
    if (this.ktpScanFilledFields.includes("pob")) {
      this.errorFields.pob_others.error = false;
    }
  }

  @action
  clearKtpScan() {
    this.cancelKtpScanPolling();
    if (this.ktpScanStatus === "SUCCESS") this.fillKtpScanFields({});
    this.ktpScanStatus = undefined;
    this.ktpScanFilledFields = [];
  }

  @action
  clearKtpUpload() {
    this.fileUpload.ktp.url = "";
    this.fileUpload.ktp.temp = undefined;
    this.errorFields.ktp_upload = { error: undefined, helperText: undefined };
    this.ktpUploadKey += 1;
  }

  get sanitizedDirectorName(): string | undefined {
    return this.passportStore?.authUser?.fullName?.replace(/[^a-z A-Z .]/g, "");
  }

  @action
  fillIdFields = (outlet: Merchant) => {
    this.form = {
      ...this.form,
      id_name: outlet.idName,
      id_number: outlet.idNumber,
      id_url: outlet.idUrl,
    };
    this.errorFields = {
      ...this.errorFields,
      id_number: {
        error: false,
      },
      id_name: {
        error: false,
      },
    };
  };

  @action
  fillForm = (outlet: Merchant, user: User) => {
    const npwpFile = outlet?.files?.find((file) =>
      file?.name?.includes(`midtrans.npwp_${outlet.businessType}`)
    );

    this.form = {
      director_name: outlet.json.director_name || user.fullName,
      files: outlet.files,
      npwp: outlet.npwp,
      npwp_name: outlet.npwpName,
      id_type: outlet.json.id_type,
      headquarters_address: outlet.headquartersAddress,
      npwp_url:
        outlet?.files?.find((file) =>
          file?.name?.includes(`midtrans.npwp_${outlet.businessType}`)
        )?.url || outlet.npwpUrl,
    };
    this.fileUpload.npwp.url = npwpFile?.url || outlet.npwpUrl;
    this.fileUpload.npwp.mediaType = npwpFile?.media_type;
    this.fileUpload.npwp.metadata = npwpFile?.metadata;

    outlet?.files?.map((file) => {
      if (file.name === `midtrans.passport`) {
        this.fileUpload.passport.url = file.url;
        this.fileUpload.passport.mediaType = file.media_type;
        this.fileUpload.passport.metadata = file.metadata;
        this.errorFields.passport_upload.error = false;
      } else if (
        file.name?.startsWith(
          `midtrans.ktp_${this.passportStore?.selectedBusinessType!}`
        )
      ) {
        this.fileUpload.ktp.url = file.url;
        this.fileUpload.ktp.mediaType = file.media_type;
        this.fileUpload.ktp.metadata = file.metadata;
        this.errorFields.ktp_upload.error = false;
      } else if (
        file.name?.startsWith(
          `midtrans.kitas_${this.passportStore?.selectedBusinessType!}`
        )
      ) {
        this.fileUpload.kitas.url = file.url;
        this.fileUpload.kitas.mediaType = file.media_type;
        this.fileUpload.kitas.metadata = file.metadata;
        this.errorFields.kitas_upload.error = false;
      }
    });
    this.fillIdFields(outlet);

    if (this.isIdTypeKitas) {
      this.selectedCitizenship = International;
    } else {
      this.selectedCitizenship = National;
    }
  };

  @action
  fillAdditionalForm = (activation: Activation) => {
    const ownerNationality = this.resolveNationality(
      activation.ownerNationality
    );

    this.additionalForm = {
      pob: activation.pob,
      pob_others: activation.pobOthers,
      dob: activation.dob,
      director_email: activation.directorEmail,
      selfie: activation.selfieUrl,
      liveness_verified: activation.livenessVerified,
      owner_nationality: ownerNationality,
      gender: activation.gender,
      occupation: activation.occupation,
    };
    this.fileUpload.selfie.url = activation.selfieUrl;
    this.errorFields = {
      ...this.errorFields,
      dob: {
        error: false,
      },
      pob: {
        error: false,
      },
      pob_others: {
        error: false,
      },
      director_email: {
        error: false,
      },
      selfie_upload: {
        error: false,
      },
      liveness_verified: {
        error: !activation.livenessVerified,
      },
      owner_nationality: {
        error: ownerNationality ? false : undefined,
      },
    };
  };

  // WNI is always Indonesia; WNA must pick a known country other than Indonesia
  resolveNationality(nationality?: string): string {
    if (this.isWni) return INDONESIA;

    return nationality && nationality !== INDONESIA && nationality in COUNTRIES
      ? nationality
      : "";
  }

  @action
  applyNationality() {
    const ownerNationality = this.resolveNationality(
      this.additionalForm.owner_nationality
    );

    this.additionalForm.owner_nationality = ownerNationality;
    this.errorFields.owner_nationality = {
      error: ownerNationality ? false : undefined,
      helperText: undefined,
    };
  }

  filteredField(keys: string[]) {
    return keys.filter((key) => key in this.errorFields);
  }

  get requiredField() {
    let _requiredField = [
      "director_name",
      "id_name",
      "dob",
      // owner nationality is only collected for individual merchants
      ...(this.showOwnerNationality ? ["owner_nationality"] : []),
    ];

    if (this.isCorporate) {
      _requiredField = [..._requiredField, "selfie_upload"];
    }

    if (this.isIndividual) {
      _requiredField = [..._requiredField, "liveness_verified"];
    }

    if (this.isWni) {
      _requiredField = [..._requiredField, "pob"];
    }

    if (this.isDirectorEmailRequired) {
      _requiredField = [..._requiredField, "director_email"];
    }

    if (this.isKtpScanActive) {
      _requiredField = [..._requiredField, "gender", "occupation"];
    }

    if (!this.isWni || this.isPobOthers) {
      _requiredField = [..._requiredField, "pob_others"];
    }

    if (
      this.isIndividualBasicAndWni ||
      this.isCorporateBasicAndWni ||
      this.isCorporateCardAndWni
    ) {
      _requiredField = [..._requiredField, "id_number", "ktp_upload"];
    }

    if (this.isIndividualBasicAndWna) {
      _requiredField = [..._requiredField, "kitas_upload", "passport_upload"];
    }

    if (this.isIndividualCardAndWni) {
      _requiredField = [
        ..._requiredField,
        "id_number",
        "ktp_upload",
        "npwp",
        "npwp_name",
        "headquarters_address",
        "npwp_upload",
      ];
    }

    if (
      this.isIndividualCardAndWna ||
      this.isCorporateBasicAndWna ||
      this.isCorporateCardAndWna
    ) {
      _requiredField = [..._requiredField, "kitas_upload", "passport_upload"];
    }

    return this.filteredField(_requiredField);
  }

  @computed
  get isFieldNotValid() {
    if (this.isKtpScanning) return true;

    const isEmpty = (val: any) => val === "" || val === null;

    const errors = this.requiredField.map((key) => {
      if (key.endsWith("_upload")) {
        let fileUpload = this.fileUpload[key.replace("_upload", "")];
        return !fileUpload?.url;
      }

      if (
        key === "director_email" &&
        !ValidationRegex.email.test(this.additionalForm.director_email || "")
      ) {
        return true;
      }

      return (
        isEmpty(this.form[key as keyof IMerchantJSON]) ||
        isEmpty(this.additionalForm[key as keyof AdditionalOnboardingData]) ||
        this.errorFields[key]?.error !== false
      );
    });

    return errors?.includes(true);
  }

  get isWni() {
    return this.selectedCitizenship === National;
  }

  get isCorporate() {
    return this.passportStore?.selectedBusinessType === Corporate;
  }

  get isIndividual() {
    return this.passportStore?.selectedBusinessType === Individual;
  }

  get showOwnerNationality() {
    return this.isOwnerNationalityEnabled && this.isIndividual;
  }

  get isIndividualBasicAndWni() {
    return (
      this.passportStore?.selectedBusinessType === Individual &&
      this.passportStore?.selectedPaymentMethod === Basic &&
      this.selectedCitizenship === National
    );
  }

  get isIndividualBasicAndWna() {
    return (
      this.passportStore?.selectedBusinessType === Individual &&
      this.passportStore.selectedPaymentMethod === Basic &&
      this.selectedCitizenship === International
    );
  }

  get isIndividualCardAndWni() {
    return (
      this.passportStore?.selectedBusinessType === Individual &&
      this.passportStore?.selectedPaymentMethod === Card &&
      this.selectedCitizenship === National
    );
  }

  get isIndividualCardAndWna() {
    return (
      this.passportStore?.selectedBusinessType === Individual &&
      this.passportStore.selectedPaymentMethod === Card &&
      this.selectedCitizenship === International
    );
  }

  get isCorporateBasicAndWni() {
    return (
      this.passportStore?.selectedBusinessType === Corporate &&
      this.passportStore?.selectedPaymentMethod === Basic &&
      this.selectedCitizenship === National
    );
  }

  get isCorporateBasicAndWna() {
    return (
      this.passportStore?.selectedBusinessType === Corporate &&
      this.passportStore.selectedPaymentMethod === Basic &&
      this.selectedCitizenship === International
    );
  }

  get isCorporateCardAndWni() {
    return (
      this.passportStore?.selectedBusinessType === Corporate &&
      this.passportStore?.selectedPaymentMethod === Card &&
      this.selectedCitizenship === National
    );
  }

  get isCorporateCardAndWna() {
    return (
      this.passportStore?.selectedBusinessType === Corporate &&
      this.passportStore.selectedPaymentMethod === Card &&
      this.selectedCitizenship === International
    );
  }

  @computed
  get validAdditionalForm(): Partial<IActivationJSON> {
    const pob =
      this.isPobOthers || !this.isWni
        ? this.additionalForm.pob_others
        : this.additionalForm.pob;
    let validatedData: Partial<IActivationJSON> = {
      pob,
      dob: this.additionalForm.dob!!,
    };

    if (this.isDirectorEmailRequired) {
      validatedData = {
        ...validatedData,
        director_email: this.additionalForm.director_email,
      };
    }

    if (this.isKtpScanActive) {
      validatedData = {
        ...validatedData,
        gender: this.additionalForm.gender,
        occupation: this.additionalForm.occupation,
      };
    }

    if (this.isCorporate) {
      validatedData = {
        ...validatedData,
        selfie_url: this.additionalForm.selfie,
      };
    }

    if (this.isIndividual) {
      validatedData = {
        ...validatedData,
        liveness_verified: this.additionalForm.liveness_verified,
        ...(this.isOwnerNationalityEnabled
          ? { owner_nationality: this.additionalForm.owner_nationality }
          : {}),
      };
    }

    return cleanObject(validatedData);
  }

  @computed
  get validForm(): Partial<IMerchantJSON> {
    this.form.files = this.form.files?.filter((file) => {
      const prefixName = "midtrans";
      const isKitas =
        file.name! ===
        `${prefixName}.kitas_${this.passportStore?.selectedBusinessType!}`;
      const isPassport = file.name! === `${prefixName}.passport`;
      const isNpwp =
        file.name! ===
        `${prefixName}.npwp_${this.passportStore?.selectedBusinessType!}`;
      const isKtp =
        file.name! ===
        `${prefixName}.ktp_${this.passportStore?.selectedBusinessType!}`;

      if (
        this.isIndividualBasicAndWni ||
        this.isCorporateBasicAndWni ||
        this.isCorporateCardAndWni
      ) {
        return isKtp;
      }

      if (
        this.isIndividualBasicAndWna ||
        this.isIndividualCardAndWna ||
        this.isCorporateBasicAndWna ||
        this.isCorporateCardAndWna
      ) {
        return isKitas || isPassport;
      }

      if (this.isIndividualCardAndWni) {
        return isKtp || isNpwp;
      }

      return file;
    });

    this.form.id_type = this.selectedCitizenship === National ? KTP : KITAS;

    return cleanObject(this.form);
  }

  @computed
  get isPobOthers(): boolean {
    return this.additionalForm.pob === POB_OTHERS;
  }

  @action
  resetSelectedCitizenship() {
    this.selectedCitizenship = National;
    this.applyNationality();
  }

  @action
  applySelectedCitizenship(citizenship: Citizenship) {
    this.selectedCitizenship = citizenship;
    if (citizenship === National) {
      this.form.id_number = this.passportStore?.outlet?.idNumber;
    } else {
      this.form.id_number = "";
    }
    this.applyNationality();
  }

  @action
  handleOnFieldChange(fieldName: keyof IMerchantJSON, value: any) {
    this.form[fieldName] = value;
    this.errorFields[fieldName].error = false;
    this.errorFields[fieldName].helperText = "";
  }

  @action
  handleAdditionalOnFieldChange(
    fieldName: keyof AdditionalOnboardingData,
    value: any
  ) {
    this.additionalForm[fieldName] = value;
    this.errorFields[fieldName].error = false;
    this.errorFields[fieldName].helperText = "";
  }

  @action
  handleLivenessVerified() {
    this.additionalForm.liveness_verified = true;
    this.errorFields.liveness_verified.error = false;
  }

  @action
  handleInvalidDateInput() {
    this.additionalForm.dob = null;
    this.errorFields.dob.error = true;
    this.errorFields.dob.helperText = "Date invalid";
  }

  @action
  validateForm = (key: any, idValidation?: "id_number" | "npwp") => {
    const keyIMerchant = key as keyof IMerchantJSON;
    const isEmptyValue = !this.form[keyIMerchant];
    let emptyValueHelperText =
      this.translate!(`owner_info.errors.${keyIMerchant}.required`) ??
      this.translate!("generic.input.error.required");
    let helperText = isEmptyValue ? emptyValueHelperText : undefined;
    let error = isEmptyValue;

    if (idValidation) {
      const idForm = this.form[keyIMerchant]?.toString().trim();
      const validateId = /^\S\d{15,15}$/;
      const validateNpwp = /^\S\d{14,15}$/;
      const isIdFormValid =
        idValidation === "id_number"
          ? validateId.test(idForm!)
          : validateNpwp.test(idForm!);

      if (!isIdFormValid) {
        error = true;
        helperText = isEmptyValue
          ? emptyValueHelperText
          : this.translate!(`owner_info.input.error.${idValidation}`);
      }
    }

    this.errorFields = {
      ...this.errorFields,
      [key]: { helperText, error },
    };
  };

  @action
  validateAdditionalForm = (key: keyof AdditionalOnboardingData) => {
    const isEmpty = !this.additionalForm[key];
    let emptyValueHelperText =
      this.translate!(`owner_info.errors.${key}.required`) ??
      this.translate!("generic.input.error.required");
    const isInvalidEmail =
      key === "director_email" &&
      !isEmpty &&
      !ValidationRegex.email.test(this.additionalForm.director_email!);

    this.errorFields = {
      ...this.errorFields,
      [key]: {
        helperText: isEmpty
          ? emptyValueHelperText
          : isInvalidEmail
          ? this.translate!("business_info.input.error.email")
          : undefined,
        error: isEmpty || isInvalidEmail,
      },
    };
  };

  @action
  deleteFileUpload = (key: string) => {
    this.fileUpload[key].url = "";
    this.fileUpload[key].temp = undefined;
    this.validateFileUpload(key);
  };

  @action
  validateFileUpload = (key: string) => {
    const isEmptyValue = !this.fileUpload[key].url;
    let helperText = isEmptyValue
      ? this.translate!("generic.input.error.required")
      : undefined;
    let error = isEmptyValue;

    this.errorFields = {
      ...this.errorFields,
      [`${key}_upload`]: { helperText, error },
    };
  };

  @action
  applyFileUpload = (file: File, name: string) => {
    this.fileUpload[name].url = this.passportStore?.fetchUrl;
    this.fileUpload[name].temp = file;
    this.validateFileUpload(name);

    const isIncludesKtp = name.toLowerCase().includes("ktp");
    const isIncludesKitas = name.toLowerCase().includes("kitas");
    const isIncludesNpwp = name.toLowerCase().includes("npwp");
    const isIncludePassport = name.toLowerCase().includes("passport");
    const isIncludesSelfie = name.toLowerCase().includes("selfie");

    if (isIncludesSelfie) {
      this.additionalForm.selfie = this.fileUpload.selfie.url;
      return;
    }

    if (isIncludesKtp || isIncludesKitas) {
      this.form.id_type = name.toUpperCase();
      this.form.id_url = this.passportStore?.fetchUrl;
    }

    if (isIncludesNpwp) {
      this.form.npwp_url = this.fileUpload.npwp.url;
    }

    const prefixName = `midtrans.${name}`;
    const fileName = isIncludePassport
      ? prefixName
      : `${prefixName}_${this.passportStore?.selectedBusinessType}`;
    this.form.files = composeFiles(this.form.files!, {
      name: fileName,
      url: this.passportStore?.fetchUrl,
      media_type: file.type,
      metadata: { tag: file.name },
    });
  };

  @action
  @asyncRequest("uploadFileKtp")
  async uploadFileKtp(userId: number, file: File) {
    if (this.isOssEnabled) {
      await this.passportStore?.uploadFileOss(file, "ktp");
    } else {
      await this.passportStore?.uploadFile(userId, file);
    }
  }

  @action
  @asyncRequest("uploadFileNpwp")
  async uploadFileNpwp(userId: number, file: File) {
    if (this.isOssEnabled) {
      await this.passportStore?.uploadFileOss(file, "npwp");
    } else {
      await this.passportStore?.uploadFile(userId, file);
    }
  }

  @action
  @asyncRequest("uploadFileKitas")
  async uploadFileKitas(userId: number, file: File) {
    if (this.isOssEnabled) {
      await this.passportStore?.uploadFileOss(file, "kitas");
    } else {
      await this.passportStore?.uploadFile(userId, file);
    }
  }

  @action
  @asyncRequest("uploadFilePassport")
  async uploadFilePassport(userId: number, file: File) {
    if (this.isOssEnabled) {
      await this.passportStore?.uploadFileOss(file, "passport");
    } else {
      await this.passportStore?.uploadFile(userId, file);
    }
  }

  @action
  @asyncRequest("uploadFileSelfie")
  async uploadFileSelfie(userId: number, file: File) {
    if (this.isOssEnabled) {
      await this.passportStore?.uploadFileOss(file, "selfie");
    } else {
      await this.passportStore?.uploadFile(userId, file);
    }
  }

  validateRequiredFields() {
    this.requiredField.forEach((key) => {
      if (key in this.additionalForm) {
        this.validateAdditionalForm(key as keyof AdditionalOnboardingData);
      } else {
        this.validateForm(key);
      }
    });
  }
}

export const OwnerInfoContext = createContext(new OwnerInfoState());

export const useOwnerInfoState = () => useContext(OwnerInfoContext);
